import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { AppContext } from "../store";
import Loading from "../common/Loading";
import { Badge, Button, Card, Col, Row, Form, Alert } from "react-bootstrap";
import { DUProduct } from "../cms/products/types/DUProduct";
import DUOrder, { Address, Attention, OrderItem } from "../pages/types/Order";
import OrderProduct from "./components/orderProduct";
import EDIT_ORDER_MUTATION from "./queries/EDIT_ORDER_MUTATION";
import { EventTypes } from "../store/actions";
import { useMutation } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import DELETE_ORDER_MUTATION from "./queries/DELETE_ORDER_MUTATION";
import OrderAddress from "./components/orderAddress";
import AlertDismissible from "../common/AlertDismissible";
import VolunteerCTA from "../common/VolunteerCTA";
import Uploader from "../common/Uploader";
import { apiRequest, loginRequest } from "../utils/authConfig";
import { useMsal, account } from "../utils/authContext";
import { getFiles } from "../cms/FileService";
import OrderFileCard from "./components/orderFileCard";
import { numberToCurrency, removeTypename } from "../utils";
import ShowHideAuth from "../common/ShowHideAuth";
import LoadingButton from "../common/LoadingButton";
import useIsAuthorized from "../hooks/useIsAuthorized";

const Order = () => {
  const { state, dispatch } = useContext(AppContext);
  const navigate = useNavigate();
  const { isAuthenticated, login } = useIsAuthorized();
  const [attention, setAttention] = useState<Attention>({
    person: "",
    city: "",
    state: "",
    zip: "",
  });
  const [fflName, setFflName] = useState<string>();
  const [validated, setValidated] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [files, setFiles] = useState<string[]>();
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [fflSaving, setFflSaving] = useState(false);
  const orderForm = useRef<HTMLFormElement>(null);
  const currentOrder = state.eventInfo.currentOrder;
  const isEC = state.orgInfo?.event_coordinator;
  const isFS = state.orgInfo?.field_support;
  const orders = state.eventInfo.orders;
  const lookupId =
    state.orgInfo?.emulated_lookup_id || state.orgInfo?.lookup_id;
  const loading = state.orgInfo?.loading || state.eventInfo.loading;
  const isVolunteer = state.orgInfo?.volunteer_access;
  const { getToken } = useMsal();
  const args = useMemo(() => ({ ...apiRequest, account }), []);
  const currentOrderId = currentOrder?.id;

  const loadFiles = useCallback(
    async (forceReload = false) => {
      if (!currentOrderId) return;
      console.log("loading files ...", currentOrderId, currentOrder);
      if ((!filesLoaded || forceReload) && !filesLoading && !loading) {
        try {
          setFilesLoading(true);
          const json = await getFiles(
            getToken,
            args,
            "/order/file",
            `${currentOrderId}`
          );
          setFiles(json.data);
        } catch (err: any) {
          setError(err.message);
        } finally {
          setFilesLoaded(true);
          setFilesLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [args, currentOrderId]
  );

  useEffect(() => {
    console.log("loadFiles?", currentOrderId);
    loadFiles(true);
  }, [currentOrderId, loadFiles]);

  useEffect(() => {
    const attn = currentOrder?.attention;
    const fflN = currentOrder?.fflName;
    if (attn) {
      setAttention(attn);
    }
    if (fflN) {
      setFflName(fflN);
    }
  }, [currentOrder?.attention, currentOrder?.fflName]);

  const addressIsComplete = (order: DUOrder) =>
    order?.address?.name &&
    order?.address?.street1 &&
    order?.address?.city &&
    order?.address?.region &&
    order?.address?.postal_code;
  const getOrderInput = (order: DUOrder) => {
    const input = { ...order } as DUOrder;
    delete (input as any).createdAt;
    delete (input as any).updatedAt;
    return removeTypename(input);
  };
  const [editOrder, { loading: editLoading }] =
    useMutation(EDIT_ORDER_MUTATION);
  const [deleteOrder] = useMutation(DELETE_ORDER_MUTATION);
  const handleQuantityChange = async (item: OrderItem) => {
    const items = currentOrder?.items.map((i: OrderItem) =>
      item.product.id === i.product.id ? item : i
    );
    const changedOrder = { ...currentOrder, items };
    const edited = orders.map((o: DUOrder) => {
      return currentOrder && currentOrder.id === o.id
        ? (changedOrder as DUOrder)
        : o;
    });
    try {
      await editOrder({
        variables: {
          orderData: getOrderInput(changedOrder as DUOrder),
        },
      });
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...edited], currentOrder: changedOrder },
      });
    } catch (err: any) {
      setError(err.message);
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...orders], currentOrder: { ...currentOrder } },
      });
      throw new Error(err.message);
    }
  };
  const handleAddressChange = async (address: Address) => {
    const changedOrder = { ...currentOrder, address };
    const edited = orders.map((o: DUOrder) => {
      return currentOrder && currentOrder.id === o.id
        ? (changedOrder as DUOrder)
        : o;
    });
    try {
      await editOrder({
        variables: {
          orderData: getOrderInput(changedOrder as DUOrder),
        },
      });

      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...edited], currentOrder: changedOrder },
      });
    } catch (err: any) {
      setError(err.message);
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...orders], currentOrder: { ...currentOrder } },
      });
      throw new Error(err.message);
    }
  };

  const handleUploaded = async () => {
    loadFiles(true);
    await saveAttentionTo();
  };

  const validateForm = () => {
    setValidated(true);
    return orderForm.current?.checkValidity();
  };

  const saveAttentionTo = async () => {
    if (validateForm() === true) {
      setFflSaving(true);
      const changedOrder = { ...currentOrder, attention, fflName };
      const edited = orders.map((o: DUOrder) => {
        return currentOrder && currentOrder.id === o.id
          ? (changedOrder as DUOrder)
          : o;
      });
      try {
        await editOrder({
          variables: {
            orderData: getOrderInput(changedOrder as DUOrder),
          },
        });

        dispatch({
          type: EventTypes.SetOrders,
          payload: { orders: [...edited], currentOrder: changedOrder },
        });
      } catch (err: any) {
        setError(err.message);
        dispatch({
          type: EventTypes.SetOrders,
          payload: { orders: [...orders], currentOrder: { ...currentOrder } },
        });
        throw new Error(err.message);
      }
      setFflSaving(false);
    }
  };
  const handleItemDelete = async (item: OrderItem) => {
    const items = currentOrder?.items.filter(
      (i: OrderItem) => item.product.id !== i.product.id
    );
    let changedRecord: DUOrder | null = null;
    const edited = orders.map((o: DUOrder) => {
      if (currentOrder && currentOrder.id === o.id) {
        changedRecord = { ...currentOrder, items: items || [] };
        return changedRecord;
      }
      return o;
    });
    if (!changedRecord) return;
    try {
      await editOrder({
        variables: {
          orderData: getOrderInput(changedRecord as DUOrder),
        },
      });
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...edited], currentOrder: changedRecord },
      });
    } catch (err: any) {
      setError(err.message);
    }
  };
  const handleOrderDelete = async (event: SyntheticEvent) => {
    event.preventDefault();
    try {
      await deleteOrder({
        variables: {
          orderId: currentOrder?.id,
        },
      });
      const edited = orders.filter((o: DUOrder) => o.id !== currentOrder?.id);
      dispatch({
        type: EventTypes.SetOrders,
        payload: {
          ...state.eventInfo,
          orders: edited.length ? [...edited] : null,
          currentOrder: null,
        },
      });
      navigate("/orders/");
    } catch (err: any) {
      setError(err.message);
    }
  };

  const submitForReview = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!currentOrder) return;
    let changedRecord: DUOrder | null = null;
    const edited = orders.map((o: DUOrder) => {
      if (currentOrder && currentOrder.id === o.id) {
        changedRecord = { ...currentOrder, state: "submitted" } as DUOrder;
        return changedRecord;
      }
      return o;
    });
    if (!changedRecord) return;
    try {
      await editOrder({
        variables: {
          orderData: getOrderInput(changedRecord),
        },
      });
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...edited], currentOrder: changedRecord },
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const placeOrder = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!currentOrder) return;
    let changedRecord: DUOrder | null = null;
    const edited = orders.map((o: DUOrder) => {
      if (currentOrder && currentOrder.id === o.id) {
        changedRecord = { ...currentOrder, state: "placed" } as DUOrder;
        return changedRecord;
      }
      return o;
    });
    if (!changedRecord) return;
    try {
      await editOrder({
        variables: {
          orderData: getOrderInput(changedRecord),
        },
      });
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...edited], currentOrder: changedRecord },
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const approveOrder = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!currentOrder) return;
    let changedRecord: DUOrder | null = null;
    const edited = orders.map((o: DUOrder) => {
      if (currentOrder && currentOrder.id === o.id) {
        changedRecord = { ...currentOrder, state: "approved" } as DUOrder;
        return changedRecord;
      }
      return o;
    });
    if (!changedRecord) return;
    try {
      await editOrder({
        variables: {
          orderData: getOrderInput(changedRecord),
        },
      });
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...edited], currentOrder: changedRecord },
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const displayProduct = (item: OrderItem) => {
    const product: DUProduct = item.product;
    return (
      <div key={product.id}>
        <hr className="m-1" />
        <div key={product.id} className="px-3">
          <Row className="align-items-center">
            <Col md="6">
              <strong>{product.code}</strong> - {product.name}
            </Col>
            <Col md="6">
              <OrderProduct
                handleDelete={handleItemDelete}
                handleQuantityChange={handleQuantityChange}
                item={item}
                loading={editLoading}
              />
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  const displayOrder = (order: DUOrder) => {
    const status = state?.eventInfo?.currentOrder?.state || "new";
    const hasFFL = !!files?.length || order.addressTo || fflName;
    const created = state?.eventInfo?.currentOrder?.createdAt || "";
    const createdAt = new Date(created).toLocaleDateString();
    const updated = state?.eventInfo?.currentOrder?.updatedAt || "";
    const updatedAt = new Date(updated).toLocaleDateString();

    return (
      <>
        <div className="d-print-none">
          {error && (
            <AlertDismissible handleClose={() => setError("")} variant="danger">
              {error}
            </AlertDismissible>
          )}
          <Badge bg="primary" pill className="mb-3">
            {status}
          </Badge>
          <Button className="float-end" onClick={() => window.print()}>
            Print Order Page
          </Button>
        </div>
        <h1 className="mb-4 d-print-none">Order Details</h1>
        <p>
          Created: {createdAt} <span className="text-secondary px-2"> | </span>{" "}
          Updated: {updatedAt} <span className="text-secondary px-2"> | </span>{" "}
          {order.orderNumber}
        </p>
        <p>Description: {order.description}</p>
        <OrderAddress
          address={order.address}
          handleChange={handleAddressChange}
        />
        <Form ref={orderForm} noValidate validated={validated}>
          <Card className={`mt-4 ${!hasFFL ? "d-print-none" : ""}`}>
            <Card.Header>
              <strong>FFL Required for All Firearm Orders</strong>
            </Card.Header>
            <Card.Body>
              {!(files && !!files.length && filesLoaded) && (
                <Alert className="my-3" variant="warning">
                  If this order contains firearms,{" "}
                  <strong>an FFL name and contact person is required</strong>.{" "}
                  <br />
                  Add the <strong>FFL name</strong>, contact{" "}
                  <strong>person's name</strong>, <strong>City</strong>, <strong>State</strong>, and{" "}
                  <strong>ZIP</strong>, and then upload your FFL file below.
                </Alert>
              )}
              {order.addressTo ? (
                <Row className="mt-2 mb-3">
                  <Col>
                    Address FFL To: <strong>{order.addressTo}</strong>
                  </Col>
                </Row>
              ) : null}
              <Row>
                <Form.Group className="mb-3" md="6" lg="4" as={Col}>
                  <Form.Label>FFL Name (Required)</Form.Label>
                  <Form.Control
                    onChange={(e) => setFflName(e.target.value)}
                    onBlur={validateForm}
                    type="text"
                    placeholder=""
                    value={fflName || ""}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    FFL Name is required.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" md="6" lg="4" as={Col}>
                  <Form.Label>Attention Of (Required)</Form.Label>
                  <Form.Control
                    onChange={(e) =>
                      setAttention({ ...attention, person: e.target.value })
                    }
                    onBlur={validateForm}
                    type="text"
                    placeholder=""
                    value={attention.person || ""}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Attention Of is required.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row>
                <Form.Group className="mb-3" md="4" lg="4" as={Col}>
                  <Form.Label>City (Required)</Form.Label>
                  <Form.Control
                    onChange={(e) =>
                      setAttention({ ...attention, city: e.target.value })
                    }
                    onBlur={validateForm}
                    type="text"
                    placeholder="City"
                    value={attention.city || ""}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    City is required.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" md="2" lg="2" as={Col}>
                  <Form.Label>State (Required)</Form.Label>
                  <Form.Control
                    onChange={(e) =>
                      setAttention({ ...attention, state: e.target.value })
                    }
                    onBlur={validateForm}
                    type="text"
                    placeholder="State"
                    value={attention.state || ""}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    State is required.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" md="2" lg="2" as={Col}>
                  <Form.Label>ZIP Code (Required)</Form.Label>
                  <Form.Control
                    onChange={(e) =>
                      setAttention({ ...attention, zip: e.target.value })
                    }
                    onBlur={validateForm}
                    type="text"
                    placeholder="ZIP Code"
                    value={attention.zip || ""}
                    maxLength={5}
                    minLength={5}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    5 digit ZIP Code is required.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              {(order.fflName !== fflName ||
                order.attention?.city !== attention?.city ||
                order.attention?.state !== attention?.state ||
                order.attention?.person !== attention?.person ||
                order.attention?.zip !== attention?.zip) && (
                <Form.Group className="mb-4" as={Row}>
                  <Col>
                    <LoadingButton
                      variant="primary"
                      onClick={saveAttentionTo}
                      style={{ width: "150px" }}
                      loading={fflSaving}
                    >
                      Save
                    </LoadingButton>
                  </Col>
                </Form.Group>
              )}
              <Row className="d-print-none">
                <Col md="6" lg="4">
                  <Uploader
                    endpoint="/order/file"
                    className="my-1"
                    label="Upload FFL"
                    validated={validated}
                    disabled={
                      !order.attention?.city ||
                      !order.attention?.state ||
                      !order.attention?.person ||
                      !order.attention?.zip 
                    }
                    disabledMessage={
                      !order.attention?.city ||
                      !order.attention?.state ||
                      !order.attention?.person ||
                      !order.attention?.zip 
                        ? "Person, City, State, and ZIP are required and must be saved before uploading an FFL."
                        : undefined
                    }
                    onUploadComplete={handleUploaded}
                    parentFolder={order.id}
                    onError={setError}
                  />
                </Col>
              </Row>
              {files && !!files.length && !filesLoading && (
                <Row className="py-0">
                  {files &&
                    files.map((f) => (
                      <Col key={f} md="6" lg="4">
                        <OrderFileCard item={f} />
                      </Col>
                    ))}
                </Row>
              )}
              {filesLoading && <Loading />}
            </Card.Body>
          </Card>
        </Form>
        <div className="mt-4">
          <h4 className="d-print-none">Ordered Items</h4>
          {order.items.map((i) => displayProduct(i))}
        </div>
        <div className="bg-light p-3 mt-5 border border-light rounded-top">
          <h5 className="m-0">
            <span className="fw-normal">Total Cost:</span>{" "}
            {numberToCurrency(
              order.items.reduce(
                (total, i) => total + i.product.cost * i.quantity,
                0
              )
            )}
          </h5>
        </div>

        <div className="d-print-none">
          {status !== "placed" && (
            <div className="bg-light p-3 border border-light rounded-bottom">
              {status === "new" && addressIsComplete(order) && (
                <ShowHideAuth
                  state={status}
                  stakeholders={order.stakeholders}
                  action="editOrderState"
                  email={state.accountInfo.username}
                >
                  <Button className="me-3" size="sm" onClick={submitForReview}>
                    Submit for Review
                  </Button>
                </ShowHideAuth>
              )}
              {status === "submitted" && (
                <ShowHideAuth
                  state={status}
                  stakeholders={order.stakeholders}
                  action="editOrderState"
                  email={state.accountInfo.username}
                >
                  <Button className="me-3" size="sm" onClick={approveOrder}>
                    Mark Order Approved
                  </Button>
                </ShowHideAuth>
              )}
              {status === "approved" && (
                <ShowHideAuth
                  state={status}
                  stakeholders={order.stakeholders}
                  action="editOrderState"
                  email={state.accountInfo.username}
                >
                  <Button className="me-3" size="sm" onClick={placeOrder}>
                    Mark Order Placed
                  </Button>
                </ShowHideAuth>
              )}
              {["new", "submitted"].includes(order?.state || "") && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    navigate(
                      `/page/ordering/?eventId=${currentOrder?.eventId}`
                    );
                  }}
                >
                  Add to Order
                </Button>
              )}
            </div>
          )}
          <ShowHideAuth
            state={status}
            stakeholders={order.stakeholders}
            action="deleteOrder"
            email={state.accountInfo.username}
          >
            {status === "placed" && <hr />}
            <div
              className="bg-light rounded p-3"
              style={{ marginTop: "144px" }}
            >
              <h4 className="text-danger mb-3">Danger Zone</h4>
              <Button variant="danger" onClick={handleOrderDelete}>
                Delete Order
              </Button>
            </div>
          </ShowHideAuth>
        </div>
      </>
    );
  };
  if (!loading && !isAuthenticated) {
    return (
      <p className="py-5">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => login(loginRequest, "loginRedirect")}
        >
          Login or Sign Up
        </Button>
      </p>
    );
  }
  if (loading) {
    return <Loading />;
  } else if (lookupId && currentOrder && currentOrder?.id) {
    if (!isVolunteer && !isEC && !isFS) {
      return <VolunteerCTA />;
    } else {
      return displayOrder(currentOrder);
    }
  }

  return (
    <>
      <p>No order to display.</p>
      <p>
        <Link to="/orders/">See open orders</Link>,{" "}
        <Link to="/events/">view your events</Link>, or{" "}
        <Link to="/">go to the home page</Link>.
      </p>
    </>
  );
};

export default Order;
